import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useMemo } from 'react';
import { FlatList, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useThemePalette } from '@/config/theme';
import { useCoinsLeaderboard } from '@/features/games/predictWeekly';
import type { CoinsLeaderboardEntry } from '@/lib/data/provider';
import { formatNumber, latinNumerals } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

const PODIUM = ['#F4C84B', '#C7CDDA', '#CD8B53'];

/** One ranked row: medal/rank, avatar, name (+ "you"), and the coin balance. */
const CoinRow = React.memo(function CoinRow({
  entry,
  youLabel,
  isFirst,
}: {
  entry: CoinsLeaderboardEntry;
  youLabel: string;
  isFirst: boolean;
}) {
  const palette = useThemePalette();
  const { locale } = useLocale();
  const nums = latinNumerals(locale);
  const medal = entry.rank <= 3 ? PODIUM[entry.rank - 1] : null;

  return (
    <View
      className={`flex-row items-center gap-3 px-4 py-3 ${isFirst ? '' : 'border-t border-line'} ${
        entry.is_me ? 'bg-volt/10' : ''
      }`}
    >
      {medal ? (
        <View
          className="h-7 w-7 items-center justify-center rounded-full"
          style={{ backgroundColor: medal }}
        >
          <AppText weight="bold" tabular style={{ color: '#1A1408', fontSize: 12 }}>
            {formatNumber(entry.rank, nums)}
          </AppText>
        </View>
      ) : (
        <AppText variant="caption" weight="bold" tabular className="w-7 text-center text-muted">
          {formatNumber(entry.rank, nums)}
        </AppText>
      )}
      <PlayerAvatar uri={entry.avatar_url} name={entry.display_name} size={34} />
      <View className="flex-1">
        <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
          {entry.display_name}
          {entry.is_me ? `  ·  ${youLabel}` : ''}
        </AppText>
      </View>
      <View className="flex-row items-center gap-1">
        <Ionicons name="logo-bitcoin" size={14} color={palette.captain} />
        <AppText variant="body" weight="bold" tabular style={{ color: palette.captain }}>
          {formatNumber(entry.coins, nums)}
        </AppText>
      </View>
    </View>
  );
});

/**
 * Seasonal Coins Leaderboard — ranks every player by their live coin balance.
 * The top N show first; the caller's own row is appended (and highlighted) when
 * they fall outside the cut, so "you're #N" is always visible.
 */
export default function CoinsLeaderboardScreen() {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const query = useCoinsLeaderboard();

  // The caller's row may be duplicated when they're inside the top N AND appended
  // for the my-rank tail; collapse on user_id so it appears once.
  const deduped = useMemo(() => {
    const rows = query.data ?? [];
    const seen = new Set<string>();
    return rows.filter((r) => {
      if (seen.has(r.user_id)) return false;
      seen.add(r.user_id);
      return true;
    });
  }, [query.data]);

  const myRank = deduped.find((r) => r.is_me)?.rank ?? null;
  const you = t('predictWeekly.board.you');

  const renderItem = useCallback(
    ({ item, index }: { item: CoinsLeaderboardEntry; index: number }) => (
      <CoinRow entry={item} youLabel={you} isFirst={index === 0} />
    ),
    [you],
  );

  return (
    <Screen>
      <BackHeader title={t('predictWeekly.board.title')} />
      <AppText variant="caption" className="mx-4 mb-1 text-center text-soft">
        {t('predictWeekly.board.intro')}
      </AppText>

      {query.isPending ? (
        <View className="gap-3 px-4 pt-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </View>
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : deduped.length === 0 ? (
        <EmptyState
          icon="trophy-outline"
          title={t('predictWeekly.board.emptyTitle')}
          body={t('predictWeekly.board.emptyBody')}
        />
      ) : (
        <View className="flex-1 pb-2">
          {myRank != null ? (
            <AppText variant="caption" weight="bold" className="mx-4 mb-2 text-center text-volt-bright">
              {t('predictWeekly.board.yourRank', {
                rank: formatNumber(myRank, numeralOptions),
              })}
            </AppText>
          ) : null}
          {/* Rows keep their single rounded `Card` frame; the inner FlatList
              virtualizes the 100s-of-rows board instead of mounting all at once. */}
          <Card className="mx-4 flex-1 overflow-hidden p-0">
            <FlatList
              data={deduped}
              keyExtractor={(entry) => entry.user_id}
              renderItem={renderItem}
              contentContainerStyle={{ paddingBottom: 16 }}
              initialNumToRender={15}
              windowSize={11}
              maxToRenderPerBatch={15}
              removeClippedSubviews
            />
          </Card>
        </View>
      )}
    </Screen>
  );
}
